import { useMemo } from 'react';
import { isProjectSelectable, liveOnly, type LiveProject } from '@wiztime/core';
import { queries } from '@wiztime/storage';
import { useLive } from './useLive';
import { useStore } from './useServices';

export interface ProjectsView {
  /** Every live project (inactive ones too: old blocks still reference them), ordered by `sort`. */
  readonly all: readonly LiveProject[];
  /**
   * Projects offered by pickers, quick mark and suggestions: active, and for a child project
   * its parent is active too (`isProjectSelectable`). Children are included; group them with
   * `buildProjectGroups`.
   */
  readonly active: readonly LiveProject[];
  readonly byId: ReadonlyMap<string, LiveProject>;
  readonly loading: boolean;
  readonly error: unknown;
  readonly retry: () => void;
}

const NO_PROJECTS: readonly LiveProject[] = [];

export function useProjects(): ProjectsView {
  const store = useStore();
  const { data, loading, error, retry } = useLive(() => queries.projects(store.repo), [store]);
  return useMemo(() => {
    const all = data === undefined ? NO_PROJECTS : liveOnly(data);
    const byId = new Map(all.map((project) => [project.id, project]));
    return {
      all,
      active: all.filter((project) => isProjectSelectable(project, byId)),
      byId,
      loading,
      error,
      retry,
    };
  }, [data, loading, error, retry]);
}
